import React, { useState } from 'react';
import {
    Typography,
    Fade,
    Zoom,
    TextField,
    Button,
    IconButton,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    Edit,
    Save,
    Cancel,
    Lock,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import 'react-toastify/dist/ReactToastify.css';
import UserService from '../../services/UserService';
import AuthService from '../../services/AuthService';
import { useNavigate } from 'react-router-dom';
import '../../css/profile.css';

// Fonction utilitaire pour vérifier les règles du mot de passe
const getPasswordErrors = (password) => {
    const errs = [];
    if (!password || password.length < 6) errs.push('Au moins 6 caractères');
    if (!password || !/[a-z]/.test(password)) errs.push('Au moins une lettre minuscule');
    if (!password || !/[A-Z]/.test(password)) errs.push('Au moins une lettre majuscule');
    if (!password || !/[0-9]/.test(password)) errs.push('Au moins un chiffre');
    if (!password || !/[^a-zA-Z0-9]/.test(password)) errs.push('Au moins un caractère spécial');
    return errs;
};

const Securite = () => {
    const navigate = useNavigate();
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const [showPassword, setShowPassword] = useState({
        currentPassword: false,
        newPassword: false,
        confirmNewPassword: false
    });

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Gestion dynamique des erreurs
        if (name === 'newPassword') {
            const pwdErrors = getPasswordErrors(value);
            setErrors(prev => ({
                ...prev,
                newPassword: pwdErrors,
                // Re-valider la confirmation si l'utilisateur change le mot de passe
                confirmNewPassword: formData.confirmNewPassword && value !== formData.confirmNewPassword
                    ? 'Les mots de passe ne correspondent pas'
                    : ''
            }));
        } else if (name === 'confirmNewPassword') {
            setErrors(prev => ({
                ...prev,
                confirmNewPassword: value !== formData.newPassword ? 'Les mots de passe ne correspondent pas' : ''
            }));
        } else if (name === 'currentPassword') {
            setErrors(prev => ({ ...prev, currentPassword: '' }));
        }
    };

    const handleClickShowPassword = (field) => {
        setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = 'Le mot de passe actuel est requis';
        }

        // Validation du nouveau mot de passe avec les règles
        const pwdErrors = getPasswordErrors(formData.newPassword);
        if (pwdErrors.length > 0) {
            newErrors.newPassword = pwdErrors;
        }

        if (!formData.confirmNewPassword) {
            newErrors.confirmNewPassword = 'La confirmation du mot de passe est requise';
        } else if (formData.newPassword !== formData.confirmNewPassword) {
            newErrors.confirmNewPassword = 'Les mots de passe ne correspondent pas';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast.error('Veuillez corriger les erreurs dans le formulaire', {
                position: "top-right",
                autoClose: 5000,
            });
            return;
        }

        setLoading(true);
        try {
            const currentUser = AuthService.getCurrentUser();

            if (!currentUser || !currentUser.id) {
                throw new Error('Utilisateur non connecté');
            }

            const changePasswordRequest = {
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword,
                confirmNewPassword: formData.confirmNewPassword
            };

            await UserService.changePassword(currentUser.id, changePasswordRequest);

            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            });

            setIsEditMode(false);

            toast.success('Mot de passe modifié avec succès !', {
                position: "top-right",
                autoClose: 3000,
            });

            Swal.fire({
                title: 'Déconnexion automatique',
                html: 'Pour des raisons de sécurité, vous serez déconnecté automatiquement dans <strong>6 secondes</strong>.',
                icon: 'info',
                timer: 6000,
                timerProgressBar: true,
                showConfirmButton: false,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                },
                willClose: () => {
                    AuthService.logout();

                    toast.info('Déconnexion réussie. Veuillez vous reconnecter avec votre nouveau mot de passe.', {
                        position: "top-right",
                        autoClose: 4000,
                    });

                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 100);
                }
            });

        } catch (error) {
            console.error('Erreur lors du changement de mot de passe:', error);

            let errorMessage = 'Erreur lors du changement de mot de passe';

            if (error.response) {
                if (error.response.status === 400) {
                    errorMessage = 'Le mot de passe actuel est incorrect';
                } else if (error.response.status === 401) {
                    errorMessage = 'Non autorisé';
                } else {
                    errorMessage = error.response.data?.message || errorMessage;
                }
            }

            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 7000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setFormData({
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: ''
        });
        setErrors({});
    };

    const getPasswordStrength = () => {
        if (!formData.newPassword) return { text: 'Non défini', color: '#9e9e9e' };

        const length = formData.newPassword.length;
        const hasUpperCase = /[A-Z]/.test(formData.newPassword);
        const hasLowerCase = /[a-z]/.test(formData.newPassword);
        const hasNumbers = /\d/.test(formData.newPassword);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword);

        let score = 0;
        if (length >= 8) score++;
        if (hasUpperCase) score++;
        if (hasLowerCase) score++;
        if (hasNumbers) score++;
        if (hasSpecialChar) score++;

        if (score <= 2) return { text: 'Faible', color: '#f44336' };
        if (score <= 3) return { text: 'Moyen', color: '#ff9800' };
        return { text: 'Fort', color: '#4caf50' };
    };

    const passwordStrength = getPasswordStrength();

    return (
        <div className="profile-main-content">
            <Fade in timeout={1000}>
                <div className="profile-card">
                    <div className="profile-card-content">
                        <div className="profile-header">
                            <Typography variant="h4" className="profile-title">
                                Sécurité
                            </Typography>

                            {!isEditMode ? (
                                <IconButton
                                    className="profile-edit-btn"
                                    onClick={() => setIsEditMode(true)}
                                    disabled={loading}
                                >
                                    <Edit />
                                    Éditer
                                </IconButton>
                            ) : (
                                <Zoom in={isEditMode}>
                                    <div className="profile-actions">
                                        <Button
                                            variant="outlined"
                                            startIcon={<Cancel />}
                                            onClick={handleCancel}
                                            className="profile-cancel-btn"
                                            disabled={loading}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                                            onClick={handleSave}
                                            className="profile-save-btn"
                                            disabled={loading}
                                        >
                                            {loading ? 'Chargement...' : 'Enregistrer'}
                                        </Button>
                                    </div>
                                </Zoom>
                            )}
                        </div>

                        <div className="profile-form-grid">
                            <Divider sx={{ my: 2, width: '100%' }} />
                            <Typography variant="h6" className="form-section-title" sx={{ mt: 2 }}>
                                Changer le mot de passe
                            </Typography>

                            <Fade in timeout={1200}>
                                <TextField
                                    fullWidth
                                    label="Mot de passe actuel"
                                    name="currentPassword"
                                    type={showPassword.currentPassword ? "text" : "password"}
                                    value={formData.currentPassword}
                                    onChange={handleInputChange}
                                    disabled={!isEditMode || loading}
                                    variant="outlined"
                                    className="profile-text-field"
                                    error={!!errors.currentPassword}
                                    helperText={errors.currentPassword}
                                    InputProps={{
                                        startAdornment: (
                                            <div style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
                                                <Lock sx={{
                                                    color: isEditMode ? 'primary.main' : 'text.secondary',
                                                    fontSize: 20
                                                }} />
                                            </div>
                                        ),
                                        endAdornment: isEditMode && (
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => handleClickShowPassword('currentPassword')}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                disabled={loading}
                                            >
                                                {showPassword.currentPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Fade>

                            <Fade in timeout={1400}>
                                <TextField
                                    fullWidth
                                    label="Nouveau mot de passe"
                                    name="newPassword"
                                    type={showPassword.newPassword ? "text" : "password"}
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    disabled={!isEditMode || loading}
                                    variant="outlined"
                                    className="profile-text-field"
                                    error={!!errors.newPassword && errors.newPassword.length > 0}
                                    helperText={
                                        errors.newPassword && errors.newPassword.length > 0 ? (
                                            <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                                                {errors.newPassword.map((err, idx) => (
                                                    <li key={idx}>{err}</li>
                                                ))}
                                            </ul>
                                        ) : ''
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <div style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
                                                <Lock sx={{
                                                    color: isEditMode ? 'primary.main' : 'text.secondary',
                                                    fontSize: 20
                                                }} />
                                            </div>
                                        ),
                                        endAdornment: isEditMode && (
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => handleClickShowPassword('newPassword')}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                disabled={loading}
                                            >
                                                {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Fade>

                            <Fade in timeout={1600}>
                                <TextField
                                    fullWidth
                                    label="Confirmer le nouveau mot de passe"
                                    name="confirmNewPassword"
                                    type={showPassword.confirmNewPassword ? "text" : "password"}
                                    value={formData.confirmNewPassword}
                                    onChange={handleInputChange}
                                    disabled={!isEditMode || loading}
                                    variant="outlined"
                                    className="profile-text-field"
                                    error={!!errors.confirmNewPassword}
                                    helperText={errors.confirmNewPassword}
                                    InputProps={{
                                        startAdornment: (
                                            <div style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
                                                <Lock sx={{
                                                    color: isEditMode ? 'primary.main' : 'text.secondary',
                                                    fontSize: 20
                                                }} />
                                            </div>
                                        ),
                                        endAdornment: isEditMode && (
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => handleClickShowPassword('confirmNewPassword')}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                disabled={loading}
                                            >
                                                {showPassword.confirmNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Fade>
                        </div>

                        <div className="profile-stats">
                            <Fade in timeout={1500}>
                                <div className="profile-stat-item">
                                    <Typography variant="h6" className="profile-stat-value" style={{ color: passwordStrength.color }}>
                                        {passwordStrength.text}
                                    </Typography>
                                    <Typography variant="body2" className="profile-stat-label">
                                        Force du mot de passe
                                    </Typography>
                                </div>
                            </Fade>

                            <Fade in timeout={1700}>
                                <div className="profile-stat-item">
                                    <Typography variant="h6" className="profile-stat-value" style={{ color: '#2196f3' }}>
                                        {new Date().toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="body2" className="profile-stat-label">
                                        Dernière modification
                                    </Typography>
                                </div>
                            </Fade>
                        </div>
                    </div>
                </div>
            </Fade>
        </div>
    );
};

export default Securite;